import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useHistory } from "react-router-dom";
import { isSafeExternalUrl } from "@/utils/urlSafetyUtils";

import styled from "styled-components";

const ScrollStyle = styled.div`
  overflow: hidden visible;

  &::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    background-color: #f5f5f5;
  }

  &::-webkit-scrollbar {
    width: 6px;
    background-color: #f5f5f5;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #424b59;
  }

  h1 {
    font-size: 20px;
    font-weight: 500;
  }

  h2 {
    font-size: 17px;
    font-weight: 500;
  }

  h3 {
    font-size: 15px;
    font-weight: 400;
  }

  h4 {
    font-size: 12px;
    font-weight: 300;
  }

  a {
    color: var(--sensolus-ai-accent);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  /* The markdown here is AI-authored, so block spacing is set once for every
     caller instead of per panel. Browser defaults leave tables cramped and
     headings glued to the paragraph above them. */
  h1,
  h2,
  h3,
  h4 {
    margin-top: 16px;
    margin-bottom: 8px;
  }

  p {
    margin-bottom: 8px;
  }

  ul,
  ol {
    margin-bottom: 8px;
    padding-left: 20px;
  }

  li {
    margin-bottom: 4px;
  }

  hr {
    margin: 16px 0;
  }

  /* Wide tables scroll inside their own box rather than stretching the panel. */
  .snt-markdown-table {
    overflow-x: auto;
    margin: 12px 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th,
  td {
    padding: 8px 16px 8px 0;
    text-align: left;
    vertical-align: top;
  }

  th {
    font-weight: 500;
    white-space: nowrap;
    border-bottom: 1px solid var(--sensolus-Sensolus_Grey_Lighter);
  }

  td {
    border-bottom: 1px solid var(--sensolus-Sensolus_BG_Zebra);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

interface SntMarkdownProps {
  text?: string;
  className?: string;
  height?: string | number;
}

const SntMarkdown = ({ text, className = "", height }: SntMarkdownProps) => {
  const history = useHistory();

  if (!text) return <></>;

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href?: string
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (!href) return;

    // Check if it's an internal link (same origin or relative path)
    const isInternal =
      href.startsWith("/") ||
      href.startsWith(window.location.origin) ||
      (!href.startsWith("http://") &&
        !href.startsWith("https://") &&
        !href.startsWith("mailto:"));

    if (isInternal) {
      // Extract the path from the URL
      const path = href.startsWith(window.location.origin)
        ? href.substring(window.location.origin.length)
        : href;

      // Always push with a unique state to ensure navigation is detected
      // even when only query params change on the same route
      history.push(path, { navigationId: Date.now() });
    } else if (href.startsWith("mailto:") || isSafeExternalUrl(href)) {
      // External link - open in new tab. The href comes from rendered markdown,
      // which can be AI- or user-authored, so the scheme is checked first.
      window.open(href, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <ScrollStyle style={{ height: height }}>
      <Markdown
        remarkPlugins={[remarkGfm]}
        className={className}
        components={{
          a: ({ href, ...props }) => {
            // Decode HTML entities in the href to handle &amp; -> &
            const decodedHref = href ? href.replace(/&amp;/g, "&") : href;
            return (
              <a
                href={decodedHref}
                onClick={(e) => handleLinkClick(e, decodedHref)}
                {...props}
              />
            );
          },
          table: ({ node, ...props }) => (
            <div className="snt-markdown-table">
              <table {...props} />
            </div>
          ),
        }}
      >
        {text}
      </Markdown>
    </ScrollStyle>
  );
};

export default SntMarkdown;
